import { ApiCustomProduct, BaseProduct, CustomProduct } from "./../../interfaces/product";
import {
  type BlankImageData,
  type BlankProductData,
  BlankSideEnum,
  BlankSingleSideEnum,
  type PrintArea,
} from "../../interfaces/blank";
import { formatBlankSingleSideFromApi, formatBlanksDataFromApi } from "./formatDataFromApi";
import { getCustomizationImageData, getCustomizationImageSrc } from "./getCustomizationImageData";

export function getProductBlankImages<T extends BaseProduct>(product?: T) {
  if (product?.blank) {
    return product.blank.images
      .filter(({ colorHex }) => product.colors.includes(colorHex))
      .sort((a) => (a.showSide === BlankSingleSideEnum.FRONT ? -1 : 1));
  }

  return [];
}

export function getCustomizationDetails<T extends CustomProduct>(data: T, color: string) {
  const blankImages = getProductBlankImages(data);

  return [BlankSingleSideEnum.FRONT, BlankSingleSideEnum.BACK].reduce(
    (acc, side) => {
      if (data.customization?.details[side]) {
        const blankImage = blankImages.find(({ colorHex, showSide }) => colorHex === color && showSide === side);
        const customizationImageData = getCustomizationImageData({
          customization: data.customization,
          blankImage,
          side,
        });

        return {
          ...acc,
          [side]: {
            area: customizationImageData?.area,
            printArea: customizationImageData?.printArea || data.customization.details[side]?.printArea,
            imageSrc: customizationImageData?.imageSrc || getCustomizationImageSrc(data.customization, side) || "",
            productionPlacement: customizationImageData?.productionPlacement,
            blankImageSrc: blankImage?.imageSrc || "",
            blankImageSrc2: blankImage?.imageSrc2 || "",
          },
        };
      }

      return acc;
    },
    {} as {
      [k in BlankSingleSideEnum]?: {
        area?: PrintArea;
        blankImageSrc: string;
        blankImageSrc2: string;
        imageSrc: string;
        printArea: PrintArea;
      };
    },
  );
}

export const getCustomizedPrintDetails = (sides: BlankSingleSideEnum[]) => {
  const hasFrontSide = sides.some((side) => side === BlankSingleSideEnum.FRONT);
  const hasBackSide = sides.some((side) => side === BlankSingleSideEnum.BACK);

  if (hasFrontSide && hasBackSide) {
    return "Frente e costas";
  }

  if (hasFrontSide) {
    return "Frente";
  }

  if (hasBackSide) {
    return "Costas";
  }

  return "";
};

/** Converte BlankSideEnum em array de BlankSingleSideEnum */
export const getPrintSidesArray = (printSide: BlankSideEnum): BlankSingleSideEnum[] => {
  if (printSide === BlankSideEnum.FRONT_BACK) {
    return [BlankSingleSideEnum.FRONT, BlankSingleSideEnum.BACK];
  }
  if (printSide === BlankSideEnum.BACK) {
    return [BlankSingleSideEnum.BACK];
  }
  return [BlankSingleSideEnum.FRONT];
};

const getCustomizedBlankFinalPrice = (blank: BlankProductData, customizationSides: BlankSideEnum[]) => {
  let price = blank.price;

  customizationSides.forEach((side) => {
    price += blank.images.find(({ showSide }) => showSide === side)?.customizationPrice || 0;
  });

  return price;
};

export const getBlankMainImage = (
  images: (Omit<BlankImageData, "showSide"> & { showSide: BlankSideEnum })[],
  customizedSides: BlankSideEnum[],
  selectedColors?: { colorHex: string; colorName: string }[],
) => {
  const originalMainBlankImage = images.find(({ mainImage }) => mainImage);
  const preferredCustomizedSide = customizedSides.includes(BlankSideEnum.FRONT)
    ? BlankSideEnum.FRONT
    : BlankSideEnum.BACK;
  // Se houver lados personalizados, prioriza o lado da frente se existir
  // Caso contrário, usa o lado da imagem principal original
  const imageSide = customizedSides.length ? preferredCustomizedSide : originalMainBlankImage?.showSide;
  // Se houver cores selecionadas e nenhuma delas for a cor da imagem original,
  // use a primeira cor da lista selecionada
  const imageColorHex =
    selectedColors?.length && !selectedColors.find(({ colorHex }) => colorHex === originalMainBlankImage?.colorHex)
      ? selectedColors[0].colorHex
      : originalMainBlankImage?.colorHex;

  if (originalMainBlankImage?.showSide === imageSide && originalMainBlankImage?.colorHex === imageColorHex) {
    return originalMainBlankImage;
  }

  return images.find(({ showSide, colorHex }) => showSide === imageSide && colorHex === imageColorHex);
};

export const formatCustomizedBlankData = (
  blank: BlankProductData,
  customizedSides: BlankSideEnum[],
  selectedColors?: { colorHex: string; colorName: string }[],
) => {
  const mainBlankImage = getBlankMainImage(blank.images, customizedSides, selectedColors);

  const images = blank.images
    .filter(({ showSide }) => customizedSides.includes(showSide))
    .map((image) => ({
      ...image,
      mainImage: mainBlankImage?.id === image.id,
    }));

  return {
    ...blank,
    price: getCustomizedBlankFinalPrice(blank, customizedSides),
    imageSrc: mainBlankImage?.imageSrc || blank.imageSrc,
    images,
    colors: selectedColors?.length
      ? blank.colors.filter((color) => selectedColors.some(({ colorHex }) => colorHex === color))
      : blank.colors,
  };
};

/**
 * Remove aspas simples e duplas no início e fim da string,
 * limitando a 25 caracteres.
 */
export const trimQuotesAndSlice = (value: string): string => {
  if (!value) {
    return "";
  }

  const trimmed = value
    .replace(/(^["']+)|(["']+$)/g, "") // Remove aspas simples e duplas no início e fim
    .slice(0, 25);

  return trimmed;
};

/** Formata dados de produto retornados por endpoints de catálogo e carrinho. */
export const formatStoreProductData = (productData: ApiCustomProduct): CustomProduct => {
  console.log("PRODUCTdATA", productData);
  const { blank, customization, ...product } = productData;

  return {
    ...product,
    blank: blank
      ? (formatBlanksDataFromApi([blank])[0] as unknown as Omit<BlankProductData, "images"> & {
          images: BlankImageData[];
        })
      : undefined,
    customization: customization
      ? {
          ...customization,
          images: customization.images?.map((image) => ({
            ...image,
            side: formatBlankSingleSideFromApi(image.side),
          })),
          printSide: Object.values(BlankSideEnum)[customization.printSide - 1],
          mainSide: customization.mainSide === 2 ? BlankSingleSideEnum.BACK : BlankSingleSideEnum.FRONT,
        }
      : undefined,
  };
};

/**
 * Formata produto congelado em item de pedido sem depender de imagens atuais de blank.
 * Imagens históricas são resolvidas por ProductItemImage via productionImageSnapshots/imageUrls.
 */
export const formatOrderProductData = (productData: ApiCustomProduct): CustomProduct => {
  const { blank: _blank, customization, ...product } = productData;

  return {
    ...product,
    customization: customization
      ? {
          ...customization,
          images: customization.images?.map((image) => ({
            ...image,
            side: formatBlankSingleSideFromApi(image.side),
          })),
          printSide: Object.values(BlankSideEnum)[customization.printSide - 1],
          mainSide: customization.mainSide === 2 ? BlankSingleSideEnum.BACK : BlankSingleSideEnum.FRONT,
        }
      : undefined,
  };
};
